import {
  createSlice,
  createAsyncThunk,
  createSelector,
} from '@reduxjs/toolkit';
import axios from 'axios';
import { Deserializer } from 'jsonapi-serializer';
import { RootState, ThunkApi } from 'state/store';
import { Status } from 'utils/customTypes';
import { SLICE_STATUS } from 'utils/constants';
import { ProjectPermissions } from 'types/store/normalized';

interface ProjectPermissionsState {
  status?: Status;
  error?: string;
  value?: ProjectPermissions;
}

const initialState: ProjectPermissionsState = {};

interface FetchProjectPermissionsParams {
  projectIds: string[];
}

export const fetchProjectPermissions = createAsyncThunk<
  ProjectPermissions,
  FetchProjectPermissionsParams,
  ThunkApi
>(
  'project/permissions/fetchProjectPermissions',
  async (params, { rejectWithValue, extra: { getProjectPermissionsApi } }) => {
    const { projectIds } = params;
    try {
      const { data } = await getProjectPermissionsApi().getList({
        params: {
          projectIds,
        },
      });
      const deserializer = new Deserializer({
        keyForAttribute: 'camelCase',
      });
      const result: ProjectPermissions[] = await deserializer.deserialize({
        data,
      });

      return result[0];
    } catch (error) {
      if (axios.isAxiosError(error)) {
        return rejectWithValue(error.response?.data);
      }
      return rejectWithValue(error as Error);
    }
  }
);

const projectPermissionsSlice = createSlice({
  name: 'project/permissions',
  initialState,
  reducers: {},
  extraReducers: (builder) => {
    builder
      .addCase(fetchProjectPermissions.pending, (state) => {
        state.status = SLICE_STATUS.LOADING;
      })
      .addCase(fetchProjectPermissions.rejected, (state, action) => {
        state.status = SLICE_STATUS.FAILED;
        state.error = action.payload as string;
      })
      .addCase(fetchProjectPermissions.fulfilled, (state, action) => {
        state.status = SLICE_STATUS.SUCCESS;
        state.value = action.payload;
      });
  },
});

const getProjectPermissionsSelector = (state: RootState) =>
  state.projectPermissions.value;

export const selectProjectPermissions = createSelector(
  [getProjectPermissionsSelector],
  (value) => value
);

export default projectPermissionsSlice.reducer;
